/**
 * Single-source-of-truth for the admin impersonation sessionStorage record.
 *
 * The store owns the write path (it sets/clears impersonation state in
 * response to user actions); the axios request interceptor reads on every
 * request to attach the `X-Impersonate-Partner-Id` header. Both sides go
 * through this module — neither hardcodes the storage key, neither
 * duplicates the type guard.
 *
 * Keeping this as a leaf module (no imports from the store or client) is
 * deliberate: it stays in this file because the client interceptor cannot
 * import the store without forming a cycle through `customInstance`.
 */

export type Impersonation = {
  id: string
  display_name: string
}

export const IMPERSONATION_STORAGE_KEY = 'gw-affiliates.impersonation'

const isImpersonation = (value: unknown): value is Impersonation =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  'display_name' in value &&
  typeof (value as { id: unknown }).id === 'string' &&
  typeof (value as { display_name: unknown }).display_name === 'string'

export const readImpersonation = (): Impersonation | null => {
  if (typeof sessionStorage === 'undefined') {
    return null
  }
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
    if (raw === null) {
      return null
    }
    const parsed: unknown = JSON.parse(raw)

    return isImpersonation(parsed) ? parsed : null
  } catch {
    // sessionStorage unavailable / malformed JSON — fall through.
    return null
  }
}

export const writeImpersonation = (value: Impersonation | null): void => {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  if (value === null) {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
  } else {
    sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(value))
  }
}
